import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import actionsHandler from "@/store/actions/actionHandler";
import { EndpointType } from "@/constants/apiEndPoints";
import { AppThunkDispatch } from "@/store";
import { useDispatch } from "react-redux";
import CloseIcon from "@styles/all-images/icons/closeblack.svg";

// Get the action handlers for create and update

interface MasterModalProps {
  open: boolean;
  onClose: () => void;
  type: string;
  mode: "create" | "edit";
  titleConfig: {
    title: string;
    display: string;
  };
  initialData?: any;
  onSuccess: () => void;
}

const MasterModal: React.FC<MasterModalProps> = ({
  open,
  onClose,
  type,
  mode,
  titleConfig,
  initialData,
  onSuccess,
}) => {
  const dispatch = useDispatch<AppThunkDispatch>();

  const validationSchema = Yup.object({
    [titleConfig.title]: Yup.string()
      .required(`${titleConfig.display} is required`)
      .min(2, `${titleConfig.display} must be at least 2 characters`)
      .max(50, `${titleConfig.display} must be at most 50 characters`),
  });

  const handleSubmit = async (values: any) => {
    try {
      const { unAuthenticatedCall: createUpdateMaster } = actionsHandler(
        type as EndpointType,
      );
      const payload = {
        type,
        ...values,
        ...(mode === "edit" && { id: initialData?.id }),
      };
      console.log("payload: ", payload);
      const response = await dispatch(
        createUpdateMaster({
          payload,
          method: mode === "create" ? "post" : "put",
          ...(mode === "edit" && { customParams: [initialData?.id] }),
        }),
      );

      if (response.ok) {
        toast.success(
          response.data.message ||
            `${titleConfig.display} ${mode === "create" ? "created" : "updated"} successfully`,
        );
        onSuccess();
        onClose();
      } else {
        if (Array.isArray(response.data.message)) {
          response.data.message.forEach((message: string) => {
            toast.error(message);
          });
        } else {
          toast.error(response.data.message || "An unexpected error occurred");
        }
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An unexpected error occurred");
    }
  };

  const formik = useFormik({
    initialValues: {
      [titleConfig.title]: initialData?.[titleConfig.title] || "",
      is_archived:
        mode === "create" ? false : initialData?.is_archived || false,
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black/25 tw-flex tw-items-center tw-justify-center tw-z-50">
      <div className="tw-bg-white tw-rounded-2xl tw-w-80 tw-p-6">
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
          <div></div>
          <h2 className="tw-text-lg font-weight-semibold font-primary-color">
            {mode === "create"
              ? `Add New ${titleConfig.display}`
              : `Edit ${titleConfig.display}`}
          </h2>
          <button
            onClick={onClose}
            className="tw-text-gray-500 tw-hover:text-gray-700"
          >
            <img src={CloseIcon} alt="Close Icon" className="tw-w-5 tw-h-5" />
          </button>
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className="tw-space-y-4 tw-mb-6">
            <TextField
              fullWidth
              id={titleConfig.title}
              name={titleConfig.title}
              label={titleConfig.display}
              value={formik.values[titleConfig.title]}
              onChange={formik.handleChange}
              error={
                formik.touched[titleConfig.title] &&
                Boolean(formik.errors[titleConfig.title])
              }
              helperText={
                formik.touched[titleConfig.title] &&
                formik.errors[titleConfig.title]?.toString()
              }
              margin="normal"
            />
          </div>

          <div className="tw-flex tw-space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="tw-flex-1 tw-px-4 tw-py-2 tw-border font-default-primary-color tw-border-[#146C94] tw-rounded-lg tw-w-[50%] tw-text-center"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-flex tw-items-center tw-gap-2 tw-w-[50%] tw-justify-center tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
            >
              {mode === "create" ? "Create" : "Update"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MasterModal;
